import React, { useState, useEffect } from 'react';
import { string, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { IconSearch } from '../../components';

import css from './HeroSearchField.module.css';

/**
 * HeroSearchField component for the hero section
 * A simple search field for events, artists, and venues
 */
const HeroSearchField = ({
  className = null,
  rootClassName = null,
  placeholder = null,
  onSearch = null,
  ...otherProps
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  const classes = classNames(rootClassName || css.root, className);

  // Check if screen is mobile size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768); // Mobile breakpoint at 768px
    };

    // Check on mount
    checkScreenSize();

    // Add resize listener
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleSubmit = e => {
    e.preventDefault();
    const searchTerm = searchValue.trim();

    if (onSearch && searchTerm) {
      onSearch(searchTerm);
    }
  };

  const handleInputChange = e => {
    setSearchValue(e.target.value);
  };

  // Use responsive placeholder text
  const getPlaceholderText = () => {
    if (isMobile) {
      return 'Search artists or venues';
    }
    return placeholder || 'Search for events, artists, venues...';
  };

  const placeholderText = getPlaceholderText();

  return (
    <div className={classes} {...otherProps}>
      <form onSubmit={handleSubmit}>
        <div className={css.searchContainer}>
          <div className={css.inputWrapper}>
            <input
              type="text"
              className={css.searchInput}
              placeholder={placeholderText}
              value={searchValue}
              onChange={handleInputChange}
              aria-label="Search for tickets"
            />
            <button
              type="submit"
              className={css.searchButton}
              aria-label="Search"
              disabled={!searchValue.trim()}
            >
              <IconSearch className={css.searchIcon} />
              <span className={css.searchButtonText}>
                <FormattedMessage id="HeroSearchField.searchButton" />
              </span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

HeroSearchField.propTypes = {
  className: string,
  rootClassName: string,
  placeholder: string,
  onSearch: func,
};

export default HeroSearchField;
